import type { SetlistContext } from './client';
import { MOTION_GRAPHICS_CATEGORIES } from './collections';

import type { MotionGraphics } from './motion-graphics-types';

export interface LutCategoryLink {
  id: string;
  name: string;
  relative_url: string;
}

// The subset of a `motiongraphicstyles` category row we need to decide whether
// an asset belongs in a LUT category. Membership isn't stored on the asset:
// it's defined by the category's `query_base` (the same query its category page
// runs), so we replicate that match here.
interface LutCategoryDoc {
  id: string;
  name: string;
  query_base?: string;
  relative_url?: string;
}

interface CategoryQueryBase {
  Keywords?: string[];
  Style?: number[];
  Theme?: number[];
}

// LUT category rows carry no `is_lut` flag in this collection, so their `/luts`
// relative_url prefix is the only marker. Typesense's `filter_by: field:prefix*`
// is unreliable on this field (it silently returns a tiny arbitrary subset), so
// we page through the (small) collection and select LUT rows in JS, the same way
// the global search does.
const LUTS_CATEGORY_URL_PREFIX = '/luts/';
const LUTS_CATEGORY_PAGE_SIZE = 250;
const LUTS_CATEGORY_CACHE_SECONDS = 600;

const parseQueryBase = (raw?: string): CategoryQueryBase | null => {
  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    // A usable query_base must be an object; anything else (a bare
    // string/number, or JSON `null`) carries no constraint we can match on.
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    return parsed as CategoryQueryBase;
  } catch {
    return null;
  }
};

// A LUT asset is always Type 84, so the category's Type constraint is implicitly
// satisfied on this page. We only check the discriminating facets
// (keywords/style/theme): the asset must satisfy every facet the category
// constrains on, and the category must constrain on at least one of them so
// that broad "all LUTs" rows don't attach to every asset.
const assetMatchesCategory = (
  asset: MotionGraphics,
  queryBase: CategoryQueryBase
): boolean => {
  const assetKeywords = new Set(
    (asset.keywords ?? []).map((keyword) => keyword.toLowerCase())
  );
  // IDs arrive as numbers in query_base but are typed as strings on the asset,
  // so compare everything as strings.
  const assetStyleIds = new Set(
    asset.styles?.map((style) => String(style.id)) ?? []
  );
  const assetThemeIds = new Set(
    asset.themes?.map((theme) => String(theme.id)) ?? []
  );

  let hasConstraint = false;

  if (queryBase.Keywords?.length) {
    hasConstraint = true;
    const matchesKeyword = queryBase.Keywords.some((keyword) =>
      assetKeywords.has(keyword.toLowerCase())
    );
    if (!matchesKeyword) {
      return false;
    }
  }

  if (queryBase.Style?.length) {
    hasConstraint = true;
    if (!queryBase.Style.some((id) => assetStyleIds.has(String(id)))) {
      return false;
    }
  }

  if (queryBase.Theme?.length) {
    hasConstraint = true;
    if (!queryBase.Theme.some((id) => assetThemeIds.has(String(id)))) {
      return false;
    }
  }

  return hasConstraint;
};

const searchCategoryPage = (context: SetlistContext, page: number) =>
  context.client
    .collections<LutCategoryDoc>(MOTION_GRAPHICS_CATEGORIES)
    .documents()
    .search(
      {
        q: '*',
        query_by: 'name',
        include_fields: 'id,name,relative_url,query_base',
        per_page: LUTS_CATEGORY_PAGE_SIZE,
        page,
      },
      { cacheSearchResultsForSeconds: LUTS_CATEGORY_CACHE_SECONDS }
    );

// Pages through the whole `motiongraphicstyles` collection and returns only the
// LUT category rows (those whose relative_url sits under `/luts`).
const fetchLutCategoryDocs = async (
  context: SetlistContext
): Promise<LutCategoryDoc[]> => {
  const firstPage = await searchCategoryPage(context, 1);
  const pageCount = Math.ceil((firstPage.found ?? 0) / LUTS_CATEGORY_PAGE_SIZE);

  const remainingPages = await Promise.all(
    Array.from({ length: Math.max(0, pageCount - 1) }, (_, index) =>
      searchCategoryPage(context, index + 2)
    )
  );

  return [firstPage, ...remainingPages]
    .flatMap((response) => response.hits ?? [])
    .map((hit) => hit.document)
    .filter((doc) => doc.relative_url?.startsWith(LUTS_CATEGORY_URL_PREFIX));
};

// Returns the LUT categories the given asset actually belongs to, so the asset
// page can interlink to `/luts` categories rather than the shared Motion
// Graphics ones embedded on the asset.
export const fetchLutCategoriesForAsset = async (
  context: SetlistContext,
  asset: MotionGraphics
): Promise<LutCategoryLink[]> => {
  const categoryDocs = await fetchLutCategoryDocs(context);

  const matches: LutCategoryLink[] = [];

  for (const doc of categoryDocs) {
    const { id, name, relative_url } = doc;
    const queryBase = parseQueryBase(doc.query_base);

    // Skip categories with no usable relative_url or query_base: without a
    // parseable constraint we can't tell whether the asset belongs to them.
    if (relative_url && queryBase && assetMatchesCategory(asset, queryBase)) {
      matches.push({ id, name, relative_url });
    }
  }

  return matches.sort((a, b) => a.name.localeCompare(b.name));
};
